import React, { useEffect, useRef, useState } from "react";
import { DoctorDto, ScheduleSlotDto, SlotStatus } from "../../../web-api-client";
import { DoctorImage } from "../../doctor/DoctorImage";
import { Ban, MoreHorizontal, Trash2, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Button } from "../../ui/button";
import { SLOT_STATUS_UI } from "../../../config/slot-status.config";
import { cn } from "../../../lib/utils";

export function DoctorItem({
  doctor,
  slot,
  onClick,
  onStatusChange,
  onDelete,
  onUpdateCapacity,
}: {
  doctor?: DoctorDto | null;
  slot: ScheduleSlotDto;
  onClick?: () => void;
  onStatusChange?: (nextStatus: "O" | "C") => Promise<{ id: number; capacity: number; status: "O" | "C"; } | undefined>;
  onDelete?: (slot: ScheduleSlotDto) => Promise<void>;
  onUpdateCapacity?: (nextCapacity: number) => Promise<{ id: number; capacity: number; status: "O" | "C"; } | undefined>;
}) {
  const booked = slot.bookedCount ?? 0;
  const cap = slot.capacity ?? 0;
  const ui = SLOT_STATUS_UI[slot.status ?? 0];

  const [draftCap, setDraftCap] = useState<number>(cap);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const capRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing && !saving) setDraftCap(cap);
  }, [cap, editing, saving]);

  const minCap = booked;

  const cancel = () => {
    setEditing(false);
    setDraftCap(cap);
  };

  const commit = async () => {
    const next = Math.max(minCap, Math.floor(Number(draftCap || minCap)));

    if (!onUpdateCapacity || next === cap) {
      setEditing(false);
      setDraftCap(cap);
      return;
    }

    try {
      setSaving(true);

      const res = await onUpdateCapacity(next);

      if (res?.capacity != null) setDraftCap(res.capacity);

      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!doctor) return;

  return (
    <div
      onClick={(e: any) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={cn(
        "w-full text-left border border-gray-200 rounded-md py-[6px] px-2 flex items-start gap-2 relative overflow-hidden transition-colors",
        "hover:border-blue-300",
        ui.background,
      )}
    >
      <div className={cn("absolute left-0 top-0 bottom-0 w-1", ui.accent)} />

      <div className="h-10 w-10 bg-green-100 rounded-full overflow-hidden shrink-0">
        <DoctorImage doctor={doctor} className="w-full h-full object-cover" />
      </div>

      <div className="flex-1 min-w-0 pr-2">
        <p
          className="text-sm font-bold text-gray-800 line-clamp-2 leading-snug"
          title={doctor.name}
        >
          {doctor.name}
        </p>

        <div className="flex items-center gap-1 text-xs text-gray-600 font-medium mt-1">
          <Users className="w-3 h-3 text-gray-500" />
          <span className="text-gray-500">Đã đặt:</span>

          <div className="flex items-center">
            <span className="font-semibold text-gray-700">{booked}/</span>

            <input
              ref={capRef}
              type="number"
              min={minCap}
              value={draftCap}
              onFocus={() => setEditing(true)}
              onChange={(e) => setDraftCap(Number(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancel();
                }
              }}
              onBlur={() => commit()}
              className={cn(
                // nhìn như text
                "w-[50px] input-font bg-transparent text-gray-700 font-semibold tabular-nums",
                "outline-none border border-transparent rounded",
                "text-left py-1 px-1",
                // hover hiện viền dù chưa edit
                "hover:border-green-primary hover:bg-white focus:bg-white focus:border-green-primary",
                // khi đang edit thì viền rõ hơn
                editing && "border-primary/50 bg-white",
                // ẩn spinner/layout shift
                saving && "opacity-70",
              )}
            />
          </div>

          {draftCap < minCap && (
            <span className="ml-1 text-[10px] text-red-600">≥ {minCap}</span>
          )}
        </div>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 absolute top-0 right-1"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          {slot.status !== SlotStatus.BLOCKED ? (
          <DropdownMenuItem
            onClick={(e: any) => {
              e.stopPropagation();
              onStatusChange?.("C");
            }}
          >
            <Ban className="w-4 h-4 mr-2" />
            Khóa đặt lịch
          </DropdownMenuItem>
          ) : (
             <DropdownMenuItem
             onClick={(e: any) => {
                e.stopPropagation();
                onStatusChange?.("O");
              }}
            >
              <Ban className="w-4 h-4 mr-2" />
              Mở đặt lịch
            </DropdownMenuItem>
          )}


          <DropdownMenuItem
            onClick={(e: any) => {
              e.stopPropagation();
              onDelete?.(slot);
            }}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Xóa
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
